import { createReducer } from '@reduxjs/toolkit'
import {
  InvoicesData,
  InvoiceStatus,
  LoadingStatus,
  SortingOrder,
  SortingBy,
  ActiveInvoice,
  Resource,
  SingleNotificationStatus,
  Contractor,
  RejectValue,
} from 'shared/types'
import { initResource, setFailed, setPending, setSucceeded } from 'shared/utils'
import { DEFAULT_TABLE_PAGE_SIZE } from 'shared/constants/pagination'
import * as actions from './actions'
interface State {
  invoicesData: InvoicesData
  loading: LoadingStatus
  areThereNoResults: boolean
  areInvoicesInitialized: boolean
  activeInvoice: ActiveInvoice | null
  error?: string | null
  sendPreTrialPaymentRequest: Resource<Blob | null>
  sendSingleNotification: Resource<
    | (SingleNotificationStatus & {
        contractorName: Contractor['name']
      })
    | null,
    RejectValue
  >
}

const initialState: State = {
  invoicesData: {
    page: 1,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
    invoices: null,
    totalRowsCount: 0,
    selectedRows: [],
    sortingBy: SortingBy.dueDate,
    sortingOrder: SortingOrder.Asc,
  },
  areThereNoResults: false,
  areInvoicesInitialized: false,
  loading: LoadingStatus.Idle,
  error: null,
  activeInvoice: null,
  sendPreTrialPaymentRequest: initResource(null),
  sendSingleNotification: initResource(null),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(actions.getInvoices.pending, state => {
      state.invoicesData.invoices = null
      state.loading = LoadingStatus.Pending
      state.error = initialState.error
    })
    .addCase(
      actions.getInvoices.fulfilled,
      (
        state,
        {
          payload: { response },
          meta: {
            arg: {
              dto: {
                contractorIds,
                page,
                pageSize,
                sortingBy,
                sortingOrder,
                ...filters
              },
            },
          },
        }
      ) => {
        const isContractorFilterDataEmpty =
          !contractorIds ||
          (contractorIds && !(contractorIds as number[]).length)
        const areFiltersEmpty = !Object.values(filters).some(filter => !!filter)
        const areThereNoResults =
          !!isContractorFilterDataEmpty &&
          areFiltersEmpty &&
          !response.results.length
        state.invoicesData = {
          ...response,
          selectedRows: state.invoicesData.selectedRows,
          invoices: response.results,
        }
        state.areThereNoResults = areThereNoResults
        state.areInvoicesInitialized = true
        state.loading = LoadingStatus.Succeeded
        state.error = null
      }
    )
    .addCase(actions.getInvoices.rejected, (state, action) => {
      state.invoicesData.invoices = null
      state.loading = LoadingStatus.Failed
      state.error = action.error.message
    })
    .addCase(actions.switchPage, (state, { payload }) => {
      state.invoicesData.page = payload
    })
    .addCase(actions.setPageSize, (state, { payload }) => {
      state.invoicesData.pageSize = payload
    })
    .addCase(actions.toggleRow, (state, { payload }) => {
      state.invoicesData.selectedRows = state.invoicesData.selectedRows.some(
        invoice => invoice.number === payload.number
      )
        ? state.invoicesData.selectedRows.filter(
            invoice => invoice.number !== payload.number
          )
        : [...state.invoicesData.selectedRows, payload]
    })
    .addCase(actions.toggleAllRows, (state, { payload }) => {
      state.invoicesData.selectedRows = payload
        ? [
            ...state.invoicesData.selectedRows.filter(
              selected =>
                !state.invoicesData.invoices?.some(
                  invoice => invoice.id === selected.id
                )
            ),
            ...(state.invoicesData.invoices?.filter(
              invoice => !invoice.isLawsuitInProgress
            ) || []),
          ]
        : state.invoicesData.selectedRows.filter(
            selectedRow =>
              !state.invoicesData.invoices!.some(
                invoice => invoice.number === selectedRow.number
              )
          )
    })
    .addCase(actions.setSorting, (state, { payload }) => {
      state.invoicesData.sortingBy = payload.sortingBy
      state.invoicesData.sortingOrder = payload.sortingOrder
    })
    .addCase(actions.setGetInvoicesLoadingStatus, (state, { payload }) => {
      state.loading = payload
    })
    .addCase(actions.clearInvoices, state => {
      state.invoicesData.invoices = null
    })
    .addCase(actions.setActiveInvoice, (state, { payload }) => {
      state.activeInvoice = payload
    })
    .addCase(actions.sendPreTrialPaymentRequest.pending, state => {
      setPending(state.sendPreTrialPaymentRequest)
    })
    .addCase(
      actions.sendPreTrialPaymentRequest.fulfilled,
      (state, { payload, meta: { arg } }) => {
        setSucceeded(state.sendPreTrialPaymentRequest, payload)
        state.invoicesData.invoices = state.invoicesData.invoices
          ? state.invoicesData.invoices?.map(i =>
              `${i.id}` === `${arg}`
                ? {
                    ...i,
                    isPreTrialPaymentRequestSent: true,
                    invoiceStatus: InvoiceStatus.PreTrialPaymentRequestSent,
                  }
                : i
            )
          : null
      }
    )
    .addCase(actions.sendPreTrialPaymentRequest.rejected, state => {
      setFailed(state.sendPreTrialPaymentRequest)
    })
    .addCase(
      actions.sendSingleNotification.pending,
      (
        state,
        {
          meta: {
            arg: { invoiceId },
          },
        }
      ) => {
        setPending(state.sendSingleNotification)
      }
    )
    .addCase(
      actions.sendSingleNotification.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { contractorName, invoiceId },
          },
        }
      ) => {
        setSucceeded(state.sendSingleNotification, {
          ...payload,
          contractorName,
        })
        state.invoicesData.invoices = state.invoicesData.invoices
          ? state.invoicesData.invoices?.map(i =>
              `${i.id}` === `${invoiceId}`
                ? { ...i, invoiceStatus: InvoiceStatus.ActionTaken }
                : i
            )
          : null
      }
    )
    .addCase(actions.sendSingleNotification.rejected, (state, { payload }) => {
      if (payload) setFailed(state.sendSingleNotification, payload[0])
      else setFailed(state.sendSingleNotification)
    })
    .addCase(actions.resetSendSingleNotificationState, state => {
      state.sendSingleNotification = initialState.sendSingleNotification
    })
    .addCase(actions.clearRowsSelection, state => {
      state.invoicesData.selectedRows = []
    })
    .addCase(
      actions.setContractorInvoiceContactDataContactCompletionStatus,
      (state, { payload: { contractorId, isContactInfoComplete } }) => {
        state.invoicesData.invoices =
          state.invoicesData.invoices?.map(invoice =>
            contractorId === invoice.contractorId
              ? {
                  ...invoice,
                  isContactInfoComplete,
                }
              : invoice
          ) || null
      }
    )
)
